import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import { transparentize } from "polished";
import animation from "./animation";
import colors from "./colors";
import { outlines } from "./focus";
var sizes = {
    small: {
        borderRadius: 40,
        fontSize: 14,
        lineHeight: 20
    },
    medium: {
        borderRadius: 24,
        fontSize: 18,
        lineHeight: 24
    },
    large: {
        borderRadius: 40,
        fontSize: 24,
        lineHeight: 32
    },
    dsLg: {
        borderRadius: "999em",
        fontFamily: "Formular",
        fontWeight: "400",
        fontSize: "var(--font-sizes-225)",
        lineHeight: "1.35",
        letterSpacing: "0em"
    },
    dsMd: {
        borderRadius: "999em",
        fontFamily: "Formular",
        fontWeight: "400",
        fontSize: "var(--font-sizes-225)",
        lineHeight: "1.35",
        letterSpacing: "0em",
        textAlign: "left"
    },
    dsSm: {
        fontFamily: "Formular",
        borderRadius: "999em",
        fontSize: "var(--font-sizes-175)",
        lineHeight: "1.15"
    }
};
var dsCommonStyles = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    top: "-1px"
};
var spacing = {
    small: {
        paddingLeft: 19,
        paddingRight: 19,
        paddingTop: 8,
        paddingBottom: 10
    },
    medium: {
        paddingLeft: 23,
        paddingRight: 23,
        paddingTop: 9,
        paddingBottom: 13
    },
    large: {
        paddingLeft: 31,
        paddingRight: 31,
        paddingTop: 13,
        paddingBottom: 17
    },
    dsLg: _object_spread({
        paddingLeft: "var(--space-300)",
        paddingRight: "var(--space-300)",
        minHeight: "48px"
    }, dsCommonStyles),
    dsMd: _object_spread({
        paddingLeft: "var(--space-200)",
        paddingRight: "var(--space-200)",
        minHeight: "40px"
    }, dsCommonStyles),
    dsSm: _object_spread({
        paddingLeft: "var(--space-100)",
        paddingRight: "var(--space-100)",
        minHeight: "32px"
    }, dsCommonStyles)
};
var withArrowSpacing = {
    small: {
        paddingLeft: 19,
        paddingRight: 15,
        paddingTop: 8,
        paddingBottom: 10
    },
    medium: {
        paddingLeft: 23,
        paddingRight: 19,
        paddingTop: 9,
        paddingBottom: 13
    },
    large: {
        paddingLeft: 31,
        paddingRight: 21,
        paddingTop: 13,
        paddingBottom: 17
    },
    dsLg: _object_spread_props(_object_spread({
        paddingLeft: "var(--space-300)",
        paddingRight: "var(--space-300)",
        minHeight: "48px"
    }, dsCommonStyles), {
        "&::after": {
            marginLeft: "var(--space-offset-50)",
            fontSize: "var(--font-sizes-300)"
        }
    }),
    dsMd: _object_spread_props(_object_spread({
        paddingLeft: "var(--space-200)",
        paddingRight: "var(--space-200)",
        minHeight: "40px"
    }, dsCommonStyles), {
        "&::after": {
            marginLeft: "var(--space-offset-50)",
            fontSize: "var(--font-sizes-300)"
        }
    }),
    dsSm: _object_spread_props(_object_spread({
        paddingLeft: "var(--space-100)",
        paddingRight: "var(--space-100)",
        minHeight: "32px"
    }, dsCommonStyles), {
        "&::after": {
            marginLeft: "var(--space-offset-50)",
            fontSize: "var(--font-sizes-200)"
        }
    })
};
var buttons = {
    variants: {
        dsPrimary: {
            color: "var(--colors-font-tertiary)",
            background: "var(--colors-background-cta-idle)",
            ":hover": {
                background: "var(--colors-background-cta-hover)"
            },
            ":active": {
                background: "var(--colors-background-cta-active)"
            },
            ":focus": _object_spread({}, outlines.default)
        },
        dsTransparent: {
            background: "var(--colors-background-default-idle)",
            color: "var(--colors-border-cta-idle)",
            border: "1px solid var(--colors-border-cta-idle)",
            ":hover": {
                background: "var(--colors-background-default-idle)",
                borderColor: "var(--colors-border-cta-idle)"
            },
            ":active": {
                background: "var(--colors-background-default-idle)",
                borderColor: "var(--colors-border-cta-active)"
            },
            ":focus": _object_spread({}, outlines.default)
        },
        white: {
            background: colors.primaryWhite,
            color: colors.primaryIndigo,
            border: "1px solid ".concat(colors.primaryWhite),
            ":hover": {
                background: "rgba(255, 255, 255, 0.9)",
                borderColor: "transparent"
            },
            ":active": {
                background: "rgba(255, 255, 255, 0.9)",
                borderColor: "transparent"
            },
            ":focus": _object_spread({}, outlines.default)
        },
        transparent: {
            background: colors.primaryWhite,
            color: colors.primaryBlue,
            border: "1px solid ".concat(colors.primaryBlue),
            ":hover": {
                background: "rgba(69, 91, 237, 0.06)",
                borderColor: colors.primaryBlueHover
            },
            ":active": {
                background: "rgba(69, 91, 237, 0.06)",
                borderColor: colors.primaryBlueHover
            },
            ":focus": _object_spread({}, outlines.default)
        },
        transparentWhite: {
            background: "transparent",
            color: colors.primaryWhite,
            border: "1px solid ".concat(colors.primaryWhite),
            ":hover": {
                background: "transparent",
                borderColor: "rgba(255, 255, 255, 0.7)"
            },
            ":active": {
                background: "transparent",
                borderColor: "rgba(255, 255, 255, 0.7)"
            },
            ":focus": _object_spread({}, outlines.default)
        },
        primary: {
            background: colors.primaryBlue,
            color: colors.primaryWhite,
            border: "1px solid ".concat(colors.primaryBlue),
            ":hover": {
                background: colors.primaryBlueHover,
                borderColor: colors.primaryBlueHover
            },
            ":active": {
                background: colors.primaryBlueActive,
                borderColor: colors.primaryBlueActive
            },
            ":focus": _object_spread({}, outlines.default)
        },
        secondary: {
            background: colors.primaryWhite,
            color: colors.primaryIndigo,
            border: "1px solid #CDCCD7"
        },
        secondaryBlue: {
            background: colors.primaryWhite,
            border: "1px solid ".concat(colors.primaryBlue),
            color: colors.primaryBlue,
            "&:hover": {
                background: transparentize(0.94, colors.primaryBlue)
            },
            "&:active": {
                background: transparentize(0.94, colors.primaryBlue)
            },
            "&:focus": _object_spread({}, outlines.default)
        },
        dark: {
            background: "transparent",
            color: colors.primaryWhite,
            border: "1px solid rgba(255, 255, 255, 0.4)",
            transition: "border-color " + animation.transitionDuration,
            "&:hover": {
                background: "transparent",
                borderColor: "rgba(255, 255, 255, 0.1)"
            },
            "&:active": {
                background: "transparent",
                borderColor: "rgba(255, 255, 255, 0.1)"
            },
            "&:focus": _object_spread({}, outlines.default)
        },
        dropdown: {
            textAlign: "left",
            color: colors.primaryIndigo,
            background: colors.primaryWhite,
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: transparentize(0.8, colors.primaryIndigo),
            borderRadius: 40,
            "&& > span": {
                paddingRight: 48,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
            },
            "&:hover": {
                borderColor: transparentize(0.6, colors.primaryIndigo)
            },
            '&:active, &[aria-expanded="true"]': {
                borderColor: colors.primaryIndigo
            }
        }
    },
    sizes: sizes,
    spacing: spacing,
    withArrowSpacing: withArrowSpacing
};
export default buttons;
